(() => {
  class QuarkTiers extends HTMLElement {
    connectedCallback() {
      this._formId = this.dataset.formId;
      this._packOptionIndex = parseInt(this.dataset.packOptionIndex || '0');
      this._allowMix = this.dataset.allowMix === 'true';

      this._currentVariant = null;
      this._selectedTierIndex = 0;
      this._mixActive = false;
      this._mixQuantities = new Map();

      try {
        this._variantData = JSON.parse(
          this.querySelector('[data-variants-json]')?.textContent || '[]'
        );
      } catch {
        this._variantData = [];
      }

      this._tierButtons = Array.from(this.querySelectorAll('[data-tier-index]'));
      this._tiers = this._tierButtons.map((btn) => ({
        qty: parseInt(btn.dataset.qty || '1'),
        discount: parseFloat(btn.dataset.discount || '0'),
        discountAmount: parseInt(btn.dataset.discountAmount || '0'),
      }));

      this._mixToggle = this.querySelector('[data-mix-toggle]');
      this._mixPanel = this.querySelector('[data-mix-panel]');
      this._mixGrid = this.querySelector('[data-mix-grid]');
      this._mixTotalEl = this.querySelector('[data-mix-total]');

      this._form = document.getElementById(this._formId);
      this._qtyInput = this._form?.querySelector('[name="quantity"]');
      this._variantPicker = document.querySelector(
        `variant-picker[form-id="${this._formId}"]`
      );

      // Wire up tier buttons
      this._tierButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          this._selectTier(parseInt(btn.dataset.tierIndex));
        });
      });

      // Mix toggle + cancel
      this._mixToggle?.addEventListener('click', () => this._activateMix());
      this.querySelector('[data-mix-cancel]')?.addEventListener('click', () => this._cancelMix());

      // Mix stepper clicks
      this._mixGrid?.addEventListener('click', (e) => this._handleMixClick(e));

      // Variant change listener
      this._variantChangeHandler = (e) => {
        if (e.detail?.formId !== this._formId) return;
        this._currentVariant = e.detail.variant;
        this._syncVariantPicker();
        this._updateTierPrices();
        this._updateSoldOutState();
        if (this._mixActive) this._updateMixRows();
      };
      document.addEventListener('variant:change', this._variantChangeHandler);

      // Form submit interceptor — handles both mix and multi-unit tier modes.
      // We intercept whenever qty > 1 because the theme's buy-buttons element
      // reads quantity from its own stepper state, not from the DOM input value
      // we set, so a direct API call is the only reliable path.
      this._formSubmitHandler = (e) => {
        const tier = this._tiers[this._selectedTierIndex];
        if (!tier || (tier.qty <= 1 && !this._mixActive)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this._mixActive) {
          this._submitMix();
        } else {
          this._submitSingle(tier.qty);
        }
      };
      this._form?.addEventListener('submit', this._formSubmitHandler, true);

      // Initialize
      this._resolveInitialVariant();
      this._updateMixRows();
      this._updateTierPrices();
      this._selectTier(0);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._variantChangeHandler);
      this._form?.removeEventListener('submit', this._formSubmitHandler, true);
      if (this._variantPicker) this._variantPicker.style.display = '';
    }

    // ── Setup ────────────────────────────────────────────────────────────

    _resolveInitialVariant() {
      const vpScript = document.querySelector(
        `variant-picker[form-id="${this._formId}"] script[data-variant]`
      );
      if (vpScript) {
        try { this._currentVariant = JSON.parse(vpScript.textContent); } catch { /* empty */ }
      }
    }

    // ── Tier selection ───────────────────────────────────────────────────

    _selectTier(index) {
      if (index < 0 || index >= this._tiers.length) return;
      this._selectedTierIndex = index;
      const tier = this._tiers[index];

      this._tierButtons.forEach((btn, i) => {
        const selected = i === index;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', String(selected));
      });

      // Show mix toggle only if this tier qty > 1 AND multiple variants visible
      if (this._mixToggle) {
        const showMix = this._allowMix && tier.qty > 1 && this._getVisibleVariantCount() > 1;
        this._mixToggle.hidden = !showMix;
      }

      if (this._mixActive) this._cancelMix();
      else if (this._qtyInput) this._qtyInput.value = tier.qty;

      this._updateSoldOutState();
    }

    _getVisibleVariantCount() {
      if (!this._currentVariant) return this._variantData.length;
      return this._variantData.filter((v) => this._variantMatchesCurrent(v)).length;
    }

    // Disable the ATC button when a multi-unit tier is selected but the current
    // variant is unavailable. The theme handles single-unit sold-out state natively;
    // we own the multi-unit submit path so we handle it ourselves here.
    _updateSoldOutState() {
      const tier = this._tiers[this._selectedTierIndex];
      if (!tier || tier.qty <= 1 || this._mixActive) {
        this._setSoldOut(false);
        return;
      }
      this._setSoldOut(this._currentVariant != null && !this._currentVariant.available);
    }

    _setSoldOut(soldOut) {
      const btn = this._getAtcButton();
      if (!btn) return;
      if (soldOut) {
        btn.setAttribute('data-quark-soldout', 'true');
        btn.disabled = true;
      } else if (btn.getAttribute('data-quark-soldout') === 'true') {
        btn.removeAttribute('data-quark-soldout');
        // Only re-enable if mix mode hasn't also disabled it
        if (btn.getAttribute('data-quark-disabled') !== 'true') {
          btn.disabled = false;
        }
      }
    }

    // Dispatch a cart:error on the product form so buy-buttons (which listens
    // on the form element, not on document) surfaces it as a notification.
    _dispatchError(message) {
      const event = new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: message },
      });
      // Prefer the form element so the theme's buy-buttons listener catches it;
      // fall back to this element if form is unavailable.
      (this._form || this).dispatchEvent(event);
    }

    _variantMatchesCurrent(variant) {
      if (!this._currentVariant) return true;
      const packIdx = this._packOptionIndex;
      for (let i = 0; i < 3; i++) {
        if (i === packIdx) continue;
        const key = `option${i + 1}`;
        const a = variant[key];
        const b = this._currentVariant[key];
        if (a && b && a !== b) return false;
      }
      return true;
    }

    // ── Pricing ──────────────────────────────────────────────────────────

    _updateTierPrices() {
      const unitPrice = this._currentVariant?.price || 0;
      this._tierButtons.forEach((btn, i) => {
        const tier = this._tiers[i];
        const gross = unitPrice * tier.qty;
        // Fixed discounts are configured in shop currency; variant prices
        // already use the customer's presentment currency.
        const rate = Number(window.Shopify?.currency?.rate);
        const convertedAmount = Number.isFinite(rate) && rate > 0
          ? Math.round(tier.discountAmount * rate) : null;

        let net;
        if (tier.discountAmount > 0) {
          // If conversion is unavailable, show gross without a savings claim.
          net = Math.max(0, gross - (convertedAmount ?? 0));
        } else if (tier.discount > 0) {
          net = Math.round(gross * (1 - tier.discount / 100));
        } else {
          net = gross;
        }

        const hasDiscount = tier.discountAmount > 0
          ? convertedAmount > 0 : tier.discount > 0;
        const priceEl = btn.querySelector('[data-tier-price]');
        const compareEl = btn.querySelector('[data-tier-compare]');
        const discountEl = btn.querySelector('[data-tier-discount-pill]');
        const badgeEl = btn.querySelector('.quark-tier__badge');
        const fixedBadge = badgeEl?.textContent.trim().match(/^save\s+\$(\d+(?:\.\d{1,2})?)$/i);
        if (tier.discountAmount > 0 && fixedBadge &&
            Math.round(Number(fixedBadge[1]) * 100) === tier.discountAmount) {
          // A merchant's literal shop-currency saving is misleading abroad.
          // The converted discount pill remains the presentment-currency claim.
          badgeEl.hidden = !hasDiscount || rate !== 1;
        }
        if (priceEl) priceEl.textContent = this._formatMoney(net);
        if (compareEl) {
          compareEl.hidden = !hasDiscount;
          if (hasDiscount) compareEl.textContent = this._formatMoney(gross);
        }
        if (discountEl) {
          discountEl.hidden = !hasDiscount;
          if (tier.discountAmount > 0 && hasDiscount) {
            discountEl.textContent = `-${this._formatMoney(convertedAmount)}`;
          }
        }
      });
    }

    // ── Mix mode ─────────────────────────────────────────────────────────

    _syncVariantPicker() {
      // Native variant changes replace the picker through product:rerender.
      this._variantPicker = document.querySelector(
        `variant-picker[form-id="${this._formId}"]`
      );
      if (this._variantPicker) {
        this._variantPicker.style.display = this._mixActive ? 'none' : '';
      }
    }

    _activateMix() {
      if (!this._allowMix) return;
      this._mixActive = true;
      this.classList.add('is-mix-active');
      this._mixPanel.hidden = false;

      this._syncVariantPicker();

      // Preseed mix with all current variant
      const tier = this._tiers[this._selectedTierIndex];
      this._mixQuantities.clear();
      if (this._currentVariant) {
        this._mixQuantities.set(String(this._currentVariant.id), tier.qty);
      }
      this._renderMixState();
    }

    _cancelMix() {
      this._mixActive = false;
      this.classList.remove('is-mix-active');
      if (this._mixPanel) this._mixPanel.hidden = true;
      this._mixQuantities.clear();
      this._syncVariantPicker();
      if (this._qtyInput) this._qtyInput.value = this._tiers[this._selectedTierIndex].qty;
      this._setAtcDisabled(false);
    }

    _updateMixRows() {
      this.querySelectorAll('[data-mix-row]').forEach((row) => {
        const variant = this._variantData.find(
          (v) => String(v.id) === row.dataset.variantId
        );
        row.hidden = variant ? !this._variantMatchesCurrent(variant) : true;
      });
    }

    _handleMixClick(e) {
      const btn = e.target.closest('[data-qty-change]');
      if (!btn || btn.disabled) return;
      const row = btn.closest('[data-mix-row]');
      if (!row) return;

      const id = row.dataset.variantId;
      const delta = parseInt(btn.dataset.qtyChange);
      const tier = this._tiers[this._selectedTierIndex];
      const current = this._mixQuantities.get(id) || 0;
      const total = this._getMixTotal();

      if (delta > 0 && total >= tier.qty) return;

      const next = Math.max(0, current + delta);
      this._mixQuantities.set(id, next);
      this._renderMixState();
    }

    _getMixTotal() {
      let total = 0;
      this._mixQuantities.forEach((q) => { total += q; });
      return total;
    }

    _renderMixState() {
      const tier = this._tiers[this._selectedTierIndex];
      const total = this._getMixTotal();
      const atCap = total >= tier.qty;

      this.querySelectorAll('[data-mix-row]').forEach((row) => {
        const id = row.dataset.variantId;
        const qty = this._mixQuantities.get(id) || 0;
        const qtyEl = row.querySelector('[data-qty-value]');
        if (qtyEl) qtyEl.textContent = qty;
        row.classList.toggle('is-active', qty > 0);

        const minus = row.querySelector('[data-qty-change="-1"]');
        if (minus) minus.disabled = qty === 0;

        const plus = row.querySelector('[data-qty-change="1"]');
        if (plus) {
          const available = row.dataset.option1 !== '' || true;
          plus.disabled = atCap || !available;
          plus.classList.toggle('is-active', qty > 0);
        }
      });

      if (this._mixTotalEl) {
        this._mixTotalEl.textContent = `${total} / ${tier.qty} allocated`;
        this._mixTotalEl.classList.toggle('is-complete', total === tier.qty);
      }

      this._setAtcDisabled(total !== tier.qty);
    }

    _setAtcDisabled(disabled) {
      const btn = this._getAtcButton();
      if (!btn) return;
      if (disabled) {
        btn.setAttribute('data-quark-disabled', 'true');
        btn.disabled = true;
      } else if (btn.getAttribute('data-quark-disabled') === 'true') {
        btn.removeAttribute('data-quark-disabled');
        btn.disabled = false;
      }
    }

    _getAtcButton() {
      return document.querySelector(
        `buy-buttons[form="${this._formId}"] button[type="submit"]`
      );
    }

    // ── Submission ───────────────────────────────────────────────────────

    async _submitSingle(qty) {
      if (!this._currentVariant) return;
      if (!this._currentVariant.available) {
        this._dispatchError('This item is currently out of stock.');
        return;
      }
      await this._cartAdd([{ id: this._currentVariant.id, quantity: qty }]);
    }

    async _submitMix() {
      const items = [];
      this._mixQuantities.forEach((qty, id) => {
        if (qty > 0) items.push({ id: parseInt(id), quantity: qty });
      });
      if (!items.length) return;
      await this._cartAdd(items, /* isMix */ true);
    }

    async _cartAdd(items, isMix = false) {
      if (this._submitting) return;
      this._submitting = true;
      const btn = this._getAtcButton();
      btn?.setAttribute('aria-busy', 'true');
      if (btn) btn.disabled = true;
      let addAccepted = false;
      let redirecting = false;

      try {
        const sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname,
          }),
        });

        addAccepted = response.ok;
        const responseJson = await response.json();

        if (response.ok) {
          if (
            window.themeVariables?.settings?.cartType === 'page' ||
            window.themeVariables?.settings?.pageType === 'cart'
          ) {
            redirecting = true;
            window.location.href = `${window.Shopify.routes.root}cart`;
            return;
          }

          const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' });
          if (!cartResponse.ok) throw new Error('Cart refresh failed after add');
          const cartData = await cartResponse.json();
          if (!Array.isArray(cartData.items) || !(cartData.item_count > 0)) {
            throw new Error('Invalid cart refresh after add');
          }
          cartData['sections'] = responseJson['sections'] || {};

          // A successful add can contain null section HTML. Retry only the
          // read; repeating cart/add would add the same products again.
          if (!this._cartSectionsReady(cartData)) {
            const sectionResponse = await fetch(
              `${window.Shopify.routes.root}?sections=${encodeURIComponent(sectionsToBundle.join(','))}`,
              { cache: 'no-store' }
            );
            if (!sectionResponse.ok) throw new Error('Cart section refresh failed');
            cartData.sections = await sectionResponse.json();
            if (!this._cartSectionsReady(cartData)) throw new Error('Cart section unavailable');
          }

          // Supply usable content before the drawer is asked to open.
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'variant:add', cart: cartData },
            })
          );
          (this._form || this).dispatchEvent(
            new CustomEvent('variant:add', {
              bubbles: true,
              detail: { items: responseJson['items'] || [], cart: cartData },
            })
          );

          if (isMix) this._cancelMix();
        } else {
          this._dispatchError(
            responseJson['description'] || 'Something went wrong. Please try again.'
          );
        }
      } catch (err) {
        console.error('[QuarkTiers]', err);
        if (addAccepted) {
          redirecting = true;
          window.location.href = `${window.Shopify.routes.root}cart`;
        } else {
          this._dispatchError('We could not confirm this add. Please check your cart before adding again.');
        }
      } finally {
        if (!redirecting) {
          this._submitting = false;
          btn?.removeAttribute('aria-busy');
          if (btn) btn.disabled = false;
        }
      }
    }

    _cartSectionsReady(cart) {
      return Array.from(document.querySelectorAll('cart-drawer')).every((drawer) => {
        const sectionId = drawer.closest('.shopify-section')?.id.replace('shopify-section-', '');
        const html = cart.sections?.[sectionId];
        if (typeof html !== 'string' || !html.trim()) return false;
        const content = new DOMParser().parseFromString(html, 'text/html');
        return !!content.querySelector('.cart-drawer') &&
          !!content.querySelector('.cart-drawer__inner') &&
          !!content.querySelector('[slot="footer"]');
      });
    }

    _formatMoney(cents) {
      try {
        const currency = window.Shopify?.currency?.active || 'USD';
        const locale = document.documentElement.lang || 'en';
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        }).format(cents / 100);
      } catch {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }
  }

  if (!window.customElements.get('quark-tiers')) {
    window.customElements.define('quark-tiers', QuarkTiers);
  }
})();
